/**
 * TemplateCenterPageV2 — proper v2 redesign (G117).
 *
 * Inventory pattern (FacetRail + Table + Slideout).
 * Reuses: getTemplates, createTemplate, updateTemplate, publishTemplate,
 * archiveTemplate, deleteTemplate, duplicateTemplate, aiGenerateTemplate.
 *
 * v1 stays as fallback via /templates?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { isRebirthBuildSectionEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, Plus, LayoutTemplate, Mail, Workflow, Bot, FileText, ShieldCheck,
  Sparkles, Copy, Edit3, Archive, Trash2, Eye, BarChart3,
  // RC-94e — Email Log tab additions.
  Send, Inbox, CheckCircle2, XCircle, AlertCircle,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TabNav,
  // RC-94b — V2 form primitives. Replaces the previous raw <input>/<select>/
  // <textarea> + inline Tailwind that was producing the visual drift
  // operators flagged on the Edit Template modal (Type vs Category).
  Input, Textarea, Select, FieldGroup,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import TemplateCenterPage from './TemplateCenterPage';

const TYPE_OPTIONS = [
  { key: 'intent',   label: 'Intent',   icon: ShieldCheck,      tone: 'trust' },
  { key: 'email',    label: 'Email',    icon: Mail,             tone: 'cyan' },
  { key: 'workflow', label: 'Workflow', icon: Workflow,         tone: 'trust' },
  { key: 'agent',    label: 'Agent',    icon: Bot,              tone: 'action' },
  { key: 'skill',    label: 'Skill',    icon: FileText,         tone: 'success' },
];
const TYPE_TONE = Object.fromEntries(TYPE_OPTIONS.map(o => [o.key, o.tone]));

const ORIGIN_OPTIONS = [
  { key: 'curated', label: 'Curated' },
  { key: 'AI',      label: 'AI-generated' },
  { key: 'tenant',  label: 'Tenant' },
];

const STATUS_OPTIONS = [
  { key: 'published', label: 'Published', tone: 'success' },
  { key: 'draft',     label: 'Draft',     tone: 'muted' },
  { key: 'archived',  label: 'Archived',  tone: 'muted' },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));

/* ─── RC-94d — AI system prompts per template type ──────────────────
 * Ported from V1 (TemplateCenterPage.jsx:44-92). The BE's
 * TemplateController.aiGenerateTemplate() expects the FE to embed the
 * system prompt and split it from the user prompt with the literal
 * "User request:" delimiter (see audit RC-94 §4). Without this, the BE
 * receives only the user's freeform text and falls back to the global
 * model config — generated templates lose all type-specific constraints
 * (no category enumeration, no brand tokens, no required-field JSON
 * shape), which is a quality regression vs V1.
 *
 * Keep these in sync with the BE's category enums for each type.
 * If categories ever move to the BE, replace this constant with a
 * fetched config — but the type-specific instructions (HTML rules,
 * markdown structure, etc.) still belong here as a FE concern. */
/* RC-95a — Brand: Prove7 (not Agentlets). Palette: navy monochrome from
 * prove7.ai live site (header #0a1d44, primary #0e3552). Keep these
 * instructions in sync with V254 seed template bodies so AI-generated
 * templates ship visually consistent with the seeded ones. */
const AI_SYSTEM_PROMPTS = {
  intent: `You are an expert policy author for Prove7 Control Vector, the governed agent orchestration platform. You author INTENT policies — the OPA/Rego gate that decides, per governed skill call, whether an agent may proceed, must be blocked, needs human approval, or must be routed to a risk-approval workflow.

Rules:
- The body MUST be a valid OPA Rego policy in package "agentlets.intent" using "import rego.v1".
- The gate evaluates the policy against these input fields: input.skill (the invoked skill name or id), input.autonomy, input.data_class, input.agent, input.tenant.
- The policy yields an OUTCOME through the "allow" rule. Two supported shapes:
  1. Boolean allow (simple allow/deny): "default allow := false" then "allow if { <conditions> }".
  2. Effect object (REQUIRED whenever the outcome is anything other than a plain allow/deny) — "allow" is an object:
       { "allow": <bool>, "effect": "<effect>", "approver_role": "<role>", "reason": "<why>", "workflow_id": "<uuid — route_workflow only>" }
     Supported effects: "allow" (proceed), "block" (deny), "require_approval" (file a human approval request), "route_workflow" (escalate to a named risk-approval workflow — this effect REQUIRES a "workflow_id" naming the target workflow UUID).
- Rules are evaluated top to bottom; the first matching rule wins, otherwise "default allow" applies. Gate each rule with a helper like:  rN_match if { input.skill in {"skill_a", "skill_b"} }
- For route_workflow, set "workflow_id" to the risk-approval workflow UUID the user names in their request. If they do not name one, use the literal placeholder "REPLACE_WITH_WORKFLOW_UUID" and say so in the description so the author fills it in before approving.
- Fail closed: the "default allow" outcome must be non-permissive (block or require_approval), never a bare allow.
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "<the Rego policy text>", "category": "...", "tags": "comma,separated", "variables": "[]"}
- The "body" field is the Rego policy TEXT — not HTML, not JSON.
- Include exactly one risk-tier keyword in tags (one of: low, medium, high) so the catalog can sort it.
- Choose category from: Access Control, Data Protection, Human-in-the-Loop, Risk Routing, Least Privilege, General`,

  email: `You are an expert email template builder for Prove7 Control Vector, the governed agent orchestration platform. You create professional HTML email templates for security operations.

Rules:
- Use the Prove7 brand: header gradient 135deg from #0a1d44 to #0e3552 (navy monochrome — NOT teal/green), primary CTA #0e3552, body text #0c1424, secondary text #4a5275, muted text #7c849c, border #d8dde7, light surface bg #eef2f8
- Font family: Inter, system-ui, sans-serif
- Max width 600px, border-radius 12px, clean professional look
- Include a navy gradient header bar with title and subtitle
- Use \${variable_name} syntax for dynamic values
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "subject": "...", "body": "<html>...", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- The body MUST be valid HTML suitable for email clients (inline styles only — no CSS variables, no <style> blocks)
- Always include a footer with the Prove7 branding
- Choose category from: Incident Alert, Escalation, Status Update, Report, Notification, General`,

  prompt: `You are an expert AI prompt engineer for Prove7 Control Vector, the governed agent orchestration platform. You create system prompts and prompt templates for security operations AI tasks.

Rules:
- Create clear, structured prompts with explicit instructions
- Use \${variable_name} syntax for dynamic input injection
- Include output format specifications (JSON, markdown, etc.)
- Define the AI persona and expertise area
- Include guardrails and constraints
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "The prompt text...", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- Choose category from: Analysis, Summarization, Classification, Response Generation, Data Extraction, General`,

  workflow: `You are an expert workflow architect for Prove7 Control Vector. You design workflow template specifications that describe reusable security automation patterns.

Rules:
- Describe workflows as JSON metadata objects (not full DAG definitions)
- Include: playbookId, category, nodes list, pattern description, connectorRequirements, estimatedDuration
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "{\\"playbookId\\":\\"...\\", ...}", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- Choose category from: Incident Response, Threat Hunting, Compliance, Remediation, Identity & Access, General
- Think about the full orchestration: trigger, enrichment, decision gates, remediation, notification`,

  response: `You are an expert incident communication writer for Prove7 Control Vector, the governed agent orchestration platform. You create structured response templates for security operations communications.

Rules:
- Use Markdown formatting for structure
- Include placeholders with \${variable_name} syntax
- Structure: header, situation/summary, impact, actions, next steps, footer
- Keep language professional but clear and actionable
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "The markdown response template...", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- Choose category from: Incident Update, Stakeholder Communication, Post-Mortem, SLA Breach, General`,
};
const buildAiPrompt = (templateType, userPrompt) => {
  const system = AI_SYSTEM_PROMPTS[templateType] || '';
  // BE splits on "User request:" — keep delimiter exact.
  return system ? `${system}\n\nUser request: ${userPrompt}` : userPrompt;
};

/* The AI generate endpoint relays the raw model output in `response`. The
 * system prompts above instruct the model to return a JSON object
 * ({name, description, body, category, tags, variables}), but models often
 * wrap it in a ```json fence or add a sentence of preamble. Recover the JSON
 * object so the preview + save read real fields instead of undefined. Returns
 * null when no JSON object can be recovered. */
const parseAiTemplateJson = (text) => {
  if (!text || typeof text !== 'string') return null;
  let s = text.trim();
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  if (s[0] !== '{') {
    const i = s.indexOf('{');
    const j = s.lastIndexOf('}');
    if (i >= 0 && j > i) s = s.slice(i, j + 1);
  }
  try { return JSON.parse(s); } catch { return null; }
};

function TemplateCenterPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTemplates?.();
      const items = Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.items) ? res.items
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res) ? res : [];
      setTemplates(items);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const [facets, setFacets] = useState(() => ({ type: new Set(), origin: new Set(), status: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => { const n = new Set(p[gKey] || []); c ? n.add(oKey) : n.delete(oKey); return { ...p, [gKey]: n }; });
  const clearFacets = () => setFacets({ type: new Set(), origin: new Set(), status: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    { key: 'type',   label: 'Type',   options: TYPE_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'origin', label: 'Origin', options: ORIGIN_OPTIONS },
    { key: 'status', label: 'Status', options: STATUS_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
  ]), []);

  const filtered = useMemo(() => templates.filter(t => {
    if (facets.type.size > 0 && !facets.type.has(t.templateType || t.type)) return false;
    if (facets.origin.size > 0 && !facets.origin.has(t.origin || (t.aiGenerated ? 'AI' : 'curated'))) return false;
    if (facets.status.size > 0 && !facets.status.has(t.status || 'published')) return false;
    return true;
  }), [templates, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = templates.filter(t => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key]; if (!sel || sel.size === 0) continue;
            if (og.key === 'type') { if (!sel.has(t.templateType || t.type)) return false; }
            else if (og.key === 'origin') { if (!sel.has(t.origin || (t.aiGenerated ? 'AI' : 'curated'))) return false; }
            else if (og.key === 'status') { if (!sel.has(t.status || 'published')) return false; }
          }
          if (g.key === 'type') return (t.templateType || t.type) === opt.key;
          if (g.key === 'origin') return (t.origin || (t.aiGenerated ? 'AI' : 'curated')) === opt.key;
          if (g.key === 'status') return (t.status || 'published') === opt.key;
          return false;
        }).length;
      }
    }
    return counts;
  }, [templates, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g, options: g.options.map(o => ({ ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const optKey of facets[g.key] || []) {
      const opt = g.options.find(o => o.key === optKey);
      chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [density, setDensity] = useState(() => { try { return localStorage.getItem('templates-density') || 'comfortable'; } catch { return 'comfortable'; } });
  const cycleDensity = () => { const n = density === 'comfortable' ? 'compact' : 'comfortable'; setDensity(n); try { localStorage.setItem('templates-density', n); } catch {} };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  const handleUseForIntent = async (tpl) => {
    try {
      const r = await api.createIntentFromTemplate?.(tpl.id);
      if (r?.ok || r?.data?.id) {
        addToast({ tone: 'success', title: 'Intent template created',
          description: 'Draft ready — edit, submit and get it approved, then bind it to an agent.' });
        const id = r?.data?.id;
        navigate(id ? `/intent-profiles?template=${id}` : '/intent-profiles');
      } else {
        addToast({ tone: 'danger', title: 'Could not create intent',
          description: r?.error || r?.data?.error || 'Requires Compliance/Policy admin, or an intent with this name already exists.' });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not create intent', description: String(e?.message || e) });
    }
  };

  const handleDuplicate = async (id) => {
    try { const r = await api.duplicateTemplate?.(id); if (r?.ok) { addToast({ tone: 'success', title: 'Duplicated' }); reload(); } }
    catch (err) { addToast({ tone: 'danger', title: 'Duplicate failed', description: err?.message }); }
  };
  const handlePublish = async (id) => {
    try { const r = await api.publishTemplate?.(id); if (r?.ok) { addToast({ tone: 'success', title: 'Published' }); reload(); } }
    catch (err) { addToast({ tone: 'danger', title: 'Publish failed', description: err?.message }); }
  };
  const handleArchive = async (id) => {
    try { const r = await api.archiveTemplate?.(id); if (r?.ok) { addToast({ tone: 'info', title: 'Archived' }); reload(); } }
    catch (err) { addToast({ tone: 'danger', title: 'Archive failed', description: err?.message }); }
  };

  /* G201 — V2-native New Template modal. Calls api.createTemplate. */
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    name: '', templateType: 'email', category: 'General', description: '', body: '',
  });
  const [newSaving, setNewSaving] = useState(false);
  const [newError, setNewError] = useState(null);
  const openNew = () => {
    setNewForm({ name: '', templateType: 'email', category: 'General', description: '', body: '' });
    setNewError(null);
    setNewOpen(true);
  };
  const submitNew = async () => {
    setNewError(null);
    if (!newForm.name.trim()) { setNewError('Name is required.'); return; }
    setNewSaving(true);
    try {
      const res = await api.createTemplate({
        name: newForm.name.trim(),
        templateType: newForm.templateType,
        category: newForm.category || 'General',
        description: newForm.description.trim() || null,
        body: newForm.body || '',
        status: 'draft',
      });
      if (!res || res.ok === false) {
        setNewError(res?.error || res?.data?.error || `Create failed (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: 'Template created' });
      setNewOpen(false);
      reload();
    } catch (e) {
      setNewError(e?.message || String(e));
    } finally {
      setNewSaving(false);
    }
  };

  /* G201 — V2-native AI Builder modal. Calls api.aiGenerateTemplate then
   * lets the operator review & save the suggested template before persisting. */
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ prompt: '', templateType: 'email' });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const openAi = () => {
    setAiForm({ prompt: '', templateType: 'email' });
    setAiError(null);
    setAiSuggestion(null);
    setAiOpen(true);
  };
  const runAi = async () => {
    setAiError(null);
    setAiSuggestion(null);
    if (!aiForm.prompt.trim()) { setAiError('Describe what you want the template to do.'); return; }
    setAiBusy(true);
    try {
      // RC-94d — Prepend type-specific system prompt. BE splits on
      // "User request:" to recover the user portion (see audit §4).
      const fullPrompt = buildAiPrompt(aiForm.templateType, aiForm.prompt.trim());
      const res = await api.aiGenerateTemplate(fullPrompt, aiForm.templateType);
      if (!res || res.ok === false) {
        setAiError(res?.error || res?.data?.error || `AI generator returned an error (HTTP ${res?.status || '?'}).`);
        return;
      }
      const payload = res.data || res;
      /* Parse the model's JSON out of `response` into the {name, body, …} shape
       * the preview + save use. Defensive: if the payload is already parsed
       * (has name/body — e.g. a future BE that returns fields directly), use it
       * unchanged so this cannot regress a working path. */
      let suggestion = payload;
      if (payload && payload.body === undefined && payload.name === undefined
          && typeof payload.response === 'string') {
        suggestion = parseAiTemplateJson(payload.response)
          || { name: '', description: '', category: 'General', body: payload.response };
      }
      setAiSuggestion(suggestion);
    } catch (e) {
      setAiError(e?.message || String(e));
    } finally {
      setAiBusy(false);
    }
  };
  const saveAiSuggestion = async () => {
    if (!aiSuggestion) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const res = await api.createTemplate({
        name: aiSuggestion.name || `AI ${aiForm.templateType} template`,
        templateType: aiForm.templateType,
        category: aiSuggestion.category || 'General',
        description: aiSuggestion.description || '',
        body: aiSuggestion.body || aiSuggestion.subject || '',
        subject: aiSuggestion.subject || null,
        tags: aiSuggestion.tags || null,
        variables: aiSuggestion.variables || null,
        status: 'draft',
        origin: 'AI',
        aiGenerated: true,
      });
      if (!res || res.ok === false) {
        setAiError(res?.error || res?.data?.error || 'Save failed.');
        return;
      }
      addToast({ tone: 'success', title: 'AI template saved' });
      setAiOpen(false);
      reload();
    } catch (e) {
      setAiError(e?.message || String(e));
    } finally {
      setAiBusy(false);
    }
  };

  /* G201 — V2-native Edit Template modal. Calls api.updateTemplate. */
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const openEdit = (t) => {
    setEditForm({
      id: t.id,
      name: t.name || t.title || '',
      templateType: t.templateType || t.type || 'email',
      category: t.category || 'General',
      description: t.description || '',
      body: t.body || t.payload || '',
      subject: t.subject || '',
    });
    setEditError(null);
    setEditOpen(true);
  };
  const submitEdit = async () => {
    setEditError(null);
    if (!editForm?.name?.trim()) { setEditError('Name is required.'); return; }
    setEditSaving(true);
    try {
      const res = await api.updateTemplate(editForm.id, {
        name: editForm.name.trim(),
        templateType: editForm.templateType,
        category: editForm.category || 'General',
        description: editForm.description.trim() || null,
        body: editForm.body || '',
        subject: editForm.subject || null,
      });
      if (!res || res.ok === false) {
        setEditError(res?.error || res?.data?.error || `Update failed (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: 'Template updated' });
      setEditOpen(false);
      reload();
    } catch (e) {
      setEditError(e?.message || String(e));
    } finally {
      setEditSaving(false);
    }
  };

  const kpis = useMemo(() => ({
    total:    templates.length,
    ai:       templates.filter(t => t.aiGenerated || t.origin === 'AI').length,
    workflow: templates.filter(t => (t.templateType || t.type) === 'workflow').length,
    agent:    templates.filter(t => (t.templateType || t.type) === 'agent').length,
  }), [templates]);

  /* ─── RC-94e — Email Log top-level tab (full V1 parity) ─────────────
   * V1 had a dedicated Email Log section with stats + log table +
   * test-send. V2 had collapsed these into Slideout.Usage (lossy —
   * delivery info gone). We re-add them as a sibling TOP-LEVEL tab so
   * Template Center now has two tabs: Templates | Email Log.
   *
   * Endpoints used (already wired in api.js):
   *   GET  /api/v1/emails              → getEmailLogs(hours,status,workflowName)
   *   GET  /api/v1/emails/stats        → getEmailStats(hours)
   *   POST /api/v1/emails/test         → sendTemplateTestEmail({templateId,to,...})
   *
   * UX pattern: status filter dropdown + hours range, stats row up top,
   * table below. Test-send is reachable from BOTH the Slideout actions
   * (when an email template is selected) AND a top-bar action on the
   * Email Log tab. */
  const [topTab, setTopTab] = useState('templates');
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailFilterStatus, setEmailFilterStatus] = useState(''); // '' = all
  const [emailFilterHours, setEmailFilterHours] = useState(168); // 7 days default
  const loadEmailLogs = useCallback(async () => {
    setEmailLoading(true);
    setEmailError(null);
    try {
      const [logsRes, statsRes] = await Promise.all([
        api.getEmailLogs(emailFilterHours, emailFilterStatus || undefined),
        api.getEmailStats(emailFilterHours),
      ]);
      if (logsRes?.ok === false) {
        setEmailError(logsRes?.error || 'Failed to load email logs.');
      } else {
        setEmailLogs(Array.isArray(logsRes?.data) ? logsRes.data : (Array.isArray(logsRes) ? logsRes : []));
      }
      if (statsRes && statsRes.ok !== false) {
        setEmailStats(statsRes.data || statsRes);
      }
    } catch (e) {
      setEmailError(e?.message || String(e));
    } finally {
      setEmailLoading(false);
    }
  }, [emailFilterHours, emailFilterStatus]);
  useEffect(() => {
    if (topTab === 'email-log') loadEmailLogs();
  }, [topTab, loadEmailLogs]);

  /* RC-94e — Test-send modal. Opens with templateId pre-selected when
   * launched from the Slideout (an email template is currently open),
   * or with templateId empty when launched from the Email Log tab
   * header (user picks). */
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ templateId: '', to: '', subject: '', variables: '{}' });
  const [testBusy, setTestBusy] = useState(false);
  const [testError, setTestError] = useState(null);
  const openTestSend = (template) => {
    setTestForm({
      templateId: template?.id || '',
      to: '',
      subject: template?.subject || '',
      variables: '{}',
    });
    setTestError(null);
    setTestOpen(true);
  };
  const submitTestSend = async () => {
    setTestError(null);
    if (!testForm.templateId) { setTestError('Pick a template to send.'); return; }
    if (!testForm.to.trim()) { setTestError('Recipient email is required.'); return; }
    let vars = {};
    try {
      vars = JSON.parse(testForm.variables || '{}');
    } catch (e) {
      setTestError('Variables must be valid JSON (e.g. {"name":"Sony"}).');
      return;
    }
    setTestBusy(true);
    try {
      const res = await api.sendTemplateTestEmail({
        templateId: testForm.templateId,
        to: testForm.to.trim(),
        subject: testForm.subject || null,
        variables: vars,
      });
      if (!res || res.ok === false) {
        setTestError(res?.error || res?.data?.error || `Send failed (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: 'Test email queued', description: `Sent to ${testForm.to}.` });
      setTestOpen(false);
      // If on Email Log tab, refresh to show the new entry.
      if (topTab === 'email-log') loadEmailLogs();
    } catch (e) {
      setTestError(e?.message || String(e));
    } finally {
      setTestBusy(false);
    }
  };

  const emailKpis = useMemo(() => {
    const s = emailStats || {};
    const total = Number(s.total ?? emailLogs.length) || 0;
    const sent = Number(s.sent ?? emailLogs.filter(l => (l.status || '').toLowerCase() === 'sent').length) || 0;
    const failed = Number(s.failed ?? emailLogs.filter(l => (l.status || '').toLowerCase() === 'failed').length) || 0;
    const rate = total > 0 ? Math.round((sent / total) * 100) : null;
    return { total, sent, failed, rate };
  }, [emailStats, emailLogs]);

  const EMAIL_STATUS_TONE = { sent: 'success', failed: 'danger', queued: 'muted', pending: 'warning' };
  const EMAIL_STATUS_ICON = { sent: CheckCircle2, failed: XCircle, queued: Inbox, pending: AlertCircle };

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Template Center</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {kpis.total} templates · {kpis.ai} AI-generated · {filtered.length} match filters
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Sparkles size={13} />}
            onClick={openAi}>AI Builder</Button>
          <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
            onClick={openNew}>New Template</Button>
        </div>
      </header>

      {/* RC-94e — Top-level tabs: Templates | Email Log. */}
      <TabNav
        tabs={[
          { id: 'templates', label: 'Templates', badge: <Pill tone="muted" size="sm">{kpis.total}</Pill> },
          { id: 'email-log', label: 'Email Log',
            badge: emailKpis.total > 0 ? <Pill tone="muted" size="sm">{emailKpis.total}</Pill> : null,
            dot: emailKpis.failed > 0 ? 'danger' : undefined },
        ]}
        activeId={topTab}
        onChange={setTopTab}
        ariaLabel="Template Center sections"
        className="mb-5"
      />

      {topTab === 'templates' && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total templates"  value={kpis.total} tone="cyan" />
        <MetricCard label="AI-generated"      value={kpis.ai} tone="trust" />
        <MetricCard label="Workflow templates" value={kpis.workflow} tone="action" />
        <MetricCard label="Agent templates"    value={kpis.agent} tone="success" />
      </div>
      )}

      {topTab === 'templates' && activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      {topTab === 'templates' && (
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Template filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<LayoutTemplate size={20} />}
            title={templates.length === 0 ? 'No templates yet' : 'No templates match filters'}
            description={templates.length === 0
              ? 'Create your first template — agent, workflow, skill, or email.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={templates.length === 0
              ? <Button variant="primary" iconLeft={<Plus />} onClick={openNew}>New Template</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Templates"
            density={density}
            columns={[
              { key: 'name', header: 'Template', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{r.name || r.title || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{(r.description || '').slice(0, 60)}</div>
                </div>
              )},
              { key: 'type', header: 'Type', render: (r) => {
                const k = r.templateType || r.type || '—';
                return <Pill tone={TYPE_TONE[k] || 'muted'} size="sm">{k}</Pill>;
              }},
              { key: 'origin', header: 'Origin', render: (r) => (
                <Pill tone={r.aiGenerated || r.origin === 'AI' ? 'trust' : 'muted'} size="sm">
                  {r.origin || (r.aiGenerated ? 'AI' : 'curated')}
                </Pill>
              )},
              { key: 'used', header: 'Used', align: 'right', render: (r) => (
                <span className="tabular-nums text-[12px]">{r.useCount || r.used || 0}</span>
              )},
              { key: 'updated', header: 'Updated', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}</span>
              )},
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {/* G367 — Was a /templates?legacy=1 bounce. Use the
                       V2 New-Agent wizard with templateId pre-selected
                       (G201's V2-native New Template + Use modal flow). */}
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/agents/new?templateId=${r.id}`)} title="Use template to create agent">Use</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDuplicate(r.id)} title="Duplicate">
                    <Copy size={13} />
                  </Button>
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>
      )}

      {topTab === 'templates' && filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="templates"
          />
        </div>
      )}

      {/* RC-94e — Email Log tab content */}
      {topTab === 'email-log' && (
        <div>
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            <MetricCard label="Total emails" value={emailKpis.total} tone="cyan" />
            <MetricCard label="Sent" value={emailKpis.sent} tone="success" />
            <MetricCard label="Failed" value={emailKpis.failed} tone={emailKpis.failed > 0 ? 'danger' : 'muted'} />
            <MetricCard label="Success rate" value={emailKpis.rate === null ? '—' : `${emailKpis.rate}%`} tone="trust" />
          </div>

          {/* Filter bar */}
          <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
            <FieldGroup label="Window" className="w-[140px] mb-0">
              <Select
                size="sm"
                value={String(emailFilterHours)}
                onChange={(e) => setEmailFilterHours(Number(e.target.value))}
              >
                <option value="24">Last 24h</option>
                <option value="168">Last 7 days</option>
                <option value="720">Last 30 days</option>
                <option value="2160">Last 90 days</option>
              </Select>
            </FieldGroup>
            <FieldGroup label="Status" className="w-[160px] mb-0">
              <Select
                size="sm"
                value={emailFilterStatus}
                onChange={(e) => setEmailFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="queued">Queued</option>
                <option value="pending">Pending</option>
              </Select>
            </FieldGroup>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={loadEmailLogs} disabled={emailLoading}>
                {emailLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Button variant="primary" size="sm" iconLeft={<Send size={13} />} onClick={() => openTestSend(null)}>
                Send test
              </Button>
            </div>
          </div>

          {emailError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px] mb-4">
              {emailError}
            </div>
          )}

          {emailLogs.length === 0 && !emailLoading ? (
            <EmptyState
              icon={<Mail size={20} />}
              title="No email deliveries in this window"
              description="Test-send a template, or widen the time window to see historical deliveries."
              primaryAction={<Button variant="primary" iconLeft={<Send />} onClick={() => openTestSend(null)}>Send test email</Button>}
            />
          ) : (
            <Table
              ariaLabel="Email log"
              density={density}
              columns={[
                { key: 'when', header: 'When', render: (r) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-muted">
                    {r.createdAt || r.sentAt ? new Date(r.createdAt || r.sentAt).toLocaleString() : '—'}
                  </span>
                )},
                { key: 'recipient', header: 'Recipient', render: (r) => (
                  <div className="text-v2-ink text-[12.5px]">{r.to || r.recipient || '—'}</div>
                )},
                { key: 'subject', header: 'Subject', render: (r) => (
                  <div>
                    <div className="text-v2-ink text-[12.5px]">{(r.subject || '—').slice(0, 80)}</div>
                    {r.workflowName && <div className="text-[10.5px] text-v2-ink-muted">via {r.workflowName}</div>}
                  </div>
                )},
                { key: 'status', header: 'Status', render: (r) => {
                  const status = String(r.status || 'unknown').toLowerCase();
                  const Icon = EMAIL_STATUS_ICON[status] || AlertCircle;
                  return (
                    <Pill tone={EMAIL_STATUS_TONE[status] || 'muted'} size="sm">
                      <Icon size={11} className="-mr-0.5" /> {status}
                    </Pill>
                  );
                }},
                { key: 'actions', header: '', align: 'right', render: (r) => (
                  r.errorMessage ? (
                    <span title={r.errorMessage} className="text-[11px] text-v2-danger underline decoration-dotted cursor-help">why?</span>
                  ) : null
                )},
              ]}
              rows={emailLogs}
              rowKey={(r) => r.id || `${r.to}-${r.createdAt}`}
            />
          )}
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Template detail'}
        width={520}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'preview',  label: 'Preview' },
          { id: 'usage',    label: 'Usage' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={TYPE_TONE[selected.templateType || selected.type] || 'muted'} size="sm">{selected.templateType || selected.type}</Pill>
              <Pill tone={selected.aiGenerated ? 'trust' : 'muted'} size="sm">{selected.origin || (selected.aiGenerated ? 'AI' : 'curated')}</Pill>
              <Pill tone={STATUS_TONE[selected.status || 'published']} size="sm" dot>{selected.status || 'published'}</Pill>
              {/* RC-94f — Display the BE-tracked version. Helps operators
                  understand a template has been edited since seed. v1 only
                  appears when the BE actually echoes a version field, so
                  pre-RC-94f rows just don't render the pill. */}
              {Number.isFinite(Number(selected.version)) && Number(selected.version) > 0 && (
                <Pill tone="muted" size="sm">v{selected.version}</Pill>
              )}
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name || selected.title}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{(selected.description || '').slice(0, 100)}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Edit3 size={13} />} onClick={() => openEdit(selected)}>Edit</Button>
              {/* Workflow templates: open the underlying playbook directly on the Workflow
                  Canvas — its existing version if one was already created from it, else fresh. */}
              {(selected.templateType || selected.type) === 'workflow' && (() => {
                let pid = null;
                try { pid = JSON.parse(selected.body || '{}')?.playbookId; } catch { /* body not JSON */ }
                return pid ? (
                  <Button variant="secondary" size="sm" iconLeft={<Workflow size={13} />}
                    onClick={() => navigate(`/canvas?playbookId=${encodeURIComponent(pid)}`)}>
                    Open in Canvas
                  </Button>
                ) : null;
              })()}
              {/* Intent templates → create a real IntentTemplate draft for binding. */}
              {(selected.templateType || selected.type) === 'intent' && (
                <Button variant="primary" size="sm" iconLeft={<ShieldCheck size={13} />} onClick={() => handleUseForIntent(selected)}>Use for intent</Button>
              )}
              {/* RC-94e — Test-send for email templates only. */}
              {(selected.templateType || selected.type) === 'email' && (
                <Button variant="secondary" size="sm" iconLeft={<Send size={13} />} onClick={() => openTestSend(selected)}>Send test</Button>
              )}
              <Button variant="ghost" size="sm" iconLeft={<Copy size={13} />} onClick={() => handleDuplicate(selected.id)}>Duplicate</Button>
              {selected.status !== 'archived' && (
                <Button variant="ghost" size="sm" className="!text-v2-danger" iconLeft={<Archive size={13} />} onClick={() => handleArchive(selected.id)}>Archive</Button>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Overview" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink leading-relaxed">{selected.description || 'No description.'}</p>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px] mt-3">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Updated</dt><dd className="text-v2-ink mt-0.5">{selected.updatedAt ? new Date(selected.updatedAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Use count</dt><dd className="text-v2-ink mt-0.5">{selected.useCount || 0}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Author</dt><dd className="text-v2-ink mt-0.5">{selected.createdBy || 'system'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'preview' && (
          /* RC-94c — Email-type templates get a real HTML iframe render
             (ported from V1 TemplateEditor:538-583). All other types fall
             back to the monospace <pre> view because they're prompts /
             JSON / markdown — not renderable as HTML.
             Iframe is sandbox="" to neutralize <script>, forms, and
             same-origin access. Template bodies are author-trusted but
             we still treat them as untrusted for defense-in-depth.
             srcDoc keeps the doctype + body wrapping consistent with how
             email clients will actually render it, so what the operator
             sees here matches what arrives in an inbox. */
          <Card padding="sm">
            <Card.Header
              title="Preview"
              subtitle={
                (selected.templateType || selected.type) === 'email'
                  ? 'Rendered HTML — what recipients see in their inbox.'
                  : (selected.templateType || selected.type) === 'workflow'
                    ? 'Workflow definition — Canvas JSON. Use “Open in Canvas” to author a workflow from it.'
                    : 'Raw body — not HTML-rendered for this template type.'
              }
            />
            <Card.Body>
              {(selected.templateType || selected.type) === 'email' ? (
                <div className="rounded-v2-md border border-v2-border bg-white overflow-hidden">
                  <iframe
                    title={`${selected.name || 'template'} preview`}
                    sandbox=""
                    srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:16px;font-family:-apple-system,Segoe UI,Inter,sans-serif;}</style></head><body>${selected.body || ''}</body></html>`}
                    style={{ width: '100%', height: 420, border: 0, display: 'block', background: '#fff' }}
                  />
                </div>
              ) : (
                <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[420px] overflow-auto whitespace-pre-wrap break-all">
                  {(() => {
                    const raw = selected.body || selected.payload || JSON.stringify(selected, null, 2);
                    // Pretty-print when the body is JSON (workflow definitions, etc.); fall back to raw text.
                    try { return JSON.stringify(JSON.parse(raw), null, 2).slice(0, 6000); }
                    catch { return String(raw).slice(0, 4000); }
                  })()}
                </pre>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'usage' && (
          <Card padding="sm">
            <Card.Header title="Usage" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink">Used {selected.useCount || 0} time{selected.useCount === 1 ? '' : 's'}.</p>
              {(selected.usedBy || []).length > 0 && (
                <ul className="space-y-1 text-[12px] mt-2">
                  {(selected.usedBy || []).map((u, i) => <li key={i} className="text-v2-ink">{u.name || u.id}</li>)}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G201 — New Template modal */}
      <Modal
        isOpen={newOpen}
        onClose={() => setNewOpen(false)}
        title="New Template"
        dismissOnBackdrop={!newSaving}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setNewOpen(false)} disabled={newSaving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submitNew} disabled={newSaving || !newForm.name.trim()}>
              {newSaving ? 'Saving…' : 'Create draft'}
            </Button>
          </div>
        }
      >
        {/* RC-94b — Rebuilt on V2 form primitives. Replaces ~60 lines of
            inline <label><span/><input/></label> + hand-rolled Tailwind. */}
        <div className="space-y-3.5">
          <FieldGroup label="Name" required>
            <Input
              value={newForm.name}
              onChange={(e) => setNewForm(f => ({ ...f, name: e.target.value }))}
              disabled={newSaving}
              autoFocus
            />
          </FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Type" hint="Type is locked once the template is created.">
              <Select
                value={newForm.templateType}
                onChange={(e) => setNewForm(f => ({ ...f, templateType: e.target.value }))}
                disabled={newSaving}
              >
                <option value="intent">Intent</option>
                <option value="email">Email</option>
                <option value="prompt">Prompt</option>
                <option value="workflow">Workflow</option>
                <option value="response">Response</option>
              </Select>
            </FieldGroup>
            <FieldGroup label="Category">
              <Input
                value={newForm.category}
                onChange={(e) => setNewForm(f => ({ ...f, category: e.target.value }))}
                disabled={newSaving}
              />
            </FieldGroup>
          </div>
          <FieldGroup label="Description">
            <Input
              value={newForm.description}
              onChange={(e) => setNewForm(f => ({ ...f, description: e.target.value }))}
              disabled={newSaving}
            />
          </FieldGroup>
          <FieldGroup label="Body">
            <Textarea
              mono
              rows={6}
              value={newForm.body}
              onChange={(e) => setNewForm(f => ({ ...f, body: e.target.value }))}
              placeholder={newForm.templateType === 'email' ? 'HTML body with ${variable} placeholders' : 'Template body (markdown / prompt / JSON)'}
              disabled={newSaving}
            />
          </FieldGroup>
          <p className="text-[11px] text-v2-ink-muted">
            Saved as <strong>draft</strong>. Edit further in the slideout, then publish from the slideout actions.
          </p>
          {newError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
              {newError}
            </div>
          )}
        </div>
      </Modal>

      {/* G201 — AI Builder modal */}
      <Modal
        isOpen={aiOpen}
        onClose={() => setAiOpen(false)}
        title="AI Template Builder"
        dismissOnBackdrop={!aiBusy}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAiOpen(false)} disabled={aiBusy}>Cancel</Button>
            {aiSuggestion ? (
              <Button variant="primary" size="sm" iconLeft={<Sparkles size={13} />} onClick={saveAiSuggestion} disabled={aiBusy}>
                {aiBusy ? 'Saving…' : 'Save as draft'}
              </Button>
            ) : (
              <Button variant="primary" size="sm" iconLeft={<Sparkles size={13} />} onClick={runAi} disabled={aiBusy || !aiForm.prompt.trim()}>
                {aiBusy ? 'Generating…' : 'Generate'}
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-3.5">
          {/* RC-94b — Rebuilt on V2 form primitives. */}
          <FieldGroup label="Template type" hint="Drives the type-specific AI system prompt.">
            <Select
              value={aiForm.templateType}
              onChange={(e) => setAiForm(f => ({ ...f, templateType: e.target.value }))}
              disabled={aiBusy || !!aiSuggestion}
            >
              <option value="intent">Intent</option>
              <option value="email">Email</option>
              <option value="prompt">Prompt</option>
              <option value="workflow">Workflow</option>
              <option value="response">Response</option>
            </Select>
          </FieldGroup>
          <FieldGroup label="Describe the template">
            <Textarea
              rows={4}
              value={aiForm.prompt}
              onChange={(e) => setAiForm(f => ({ ...f, prompt: e.target.value }))}
              placeholder="e.g. P1 incident escalation email to the security leadership team, must include incident summary, blast radius, and current containment status."
              disabled={aiBusy || !!aiSuggestion}
            />
          </FieldGroup>

          {aiSuggestion && (
            <Card padding="sm">
              <Card.Header title="Generated draft" actions={
                <Button variant="ghost" size="sm" onClick={() => { setAiSuggestion(null); }}>Discard & regenerate</Button>
              } />
              <Card.Body>
                <div className="space-y-2 text-[12.5px]">
                  <div><strong>Name:</strong> {aiSuggestion.name || '—'}</div>
                  {aiSuggestion.subject && <div><strong>Subject:</strong> {aiSuggestion.subject}</div>}
                  <div><strong>Category:</strong> {aiSuggestion.category || 'General'}</div>
                  <div><strong>Description:</strong> {aiSuggestion.description || '—'}</div>
                  <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[300px] overflow-auto whitespace-pre-wrap break-all">
                    {(aiSuggestion.body || '').slice(0, 4000)}
                  </pre>
                </div>
              </Card.Body>
            </Card>
          )}

          {aiError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
              {aiError}
            </div>
          )}
        </div>
      </Modal>

      {/* G201 — Edit Template modal */}
      <Modal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Template"
        dismissOnBackdrop={!editSaving}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submitEdit} disabled={editSaving || !editForm?.name?.trim()}>
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        }
      >
        {editForm && (
          /* RC-94b — Rebuilt on V2 form primitives. The Type field now uses
             Input readOnly={true} which renders a lock icon + tooltip
             explaining WHY the field is locked. This directly addresses
             the screenshot bug: previously Type looked "broken / disabled"
             while Category looked "active" — both used raw <input> with
             different inline classes. Now Type's immutability is a
             first-class affordance, not an inconsistency. */
          <div className="space-y-3.5">
            <FieldGroup label="Name" required>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                disabled={editSaving}
              />
            </FieldGroup>
            <div className="grid grid-cols-2 gap-3">
              <FieldGroup label="Type" hint="Locked after creation.">
                <Input
                  value={editForm.templateType || ''}
                  readOnly
                  readOnlyReason="Type can't change after a template is created — the BE rejects type changes to keep variable shape stable. Create a new template if you need a different type."
                />
              </FieldGroup>
              <FieldGroup label="Category">
                <Input
                  value={editForm.category}
                  onChange={(e) => setEditForm(f => ({ ...f, category: e.target.value }))}
                  disabled={editSaving}
                />
              </FieldGroup>
            </div>
            {editForm.templateType === 'email' && (
              <FieldGroup label="Subject">
                <Input
                  value={editForm.subject}
                  onChange={(e) => setEditForm(f => ({ ...f, subject: e.target.value }))}
                  disabled={editSaving}
                />
              </FieldGroup>
            )}
            <FieldGroup label="Description">
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                disabled={editSaving}
              />
            </FieldGroup>
            <FieldGroup label="Body">
              <Textarea
                mono
                rows={10}
                value={editForm.body}
                onChange={(e) => setEditForm(f => ({ ...f, body: e.target.value }))}
                disabled={editSaving}
              />
            </FieldGroup>
            {editError && (
              <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
                {editError}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* RC-94e — Test-send modal. Reusable from Slideout (template
          pre-selected) and from Email Log header (user picks from list).
          Variables field accepts JSON for ${variable} interpolation; the
          BE substitutes before sending. */}
      <Modal
        isOpen={testOpen}
        onClose={() => setTestOpen(false)}
        title="Send test email"
        dismissOnBackdrop={!testBusy}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setTestOpen(false)} disabled={testBusy}>Cancel</Button>
            <Button variant="primary" size="sm" iconLeft={<Send size={13} />} onClick={submitTestSend} disabled={testBusy || !testForm.to.trim() || !testForm.templateId}>
              {testBusy ? 'Sending…' : 'Send test'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3.5">
          <FieldGroup label="Template" required hint="Pre-selected when launched from a template's slideout.">
            <Select
              value={testForm.templateId}
              onChange={(e) => setTestForm(f => ({ ...f, templateId: e.target.value }))}
              disabled={testBusy}
            >
              <option value="">Select a template…</option>
              {templates.filter(t => (t.templateType || t.type) === 'email').map(t => (
                <option key={t.id} value={t.id}>{t.name || t.id}</option>
              ))}
            </Select>
          </FieldGroup>
          <FieldGroup label="Recipient email" required>
            <Input
              type="email"
              value={testForm.to}
              onChange={(e) => setTestForm(f => ({ ...f, to: e.target.value }))}
              placeholder="you@example.com"
              disabled={testBusy}
            />
          </FieldGroup>
          <FieldGroup label="Subject override" hint="Leave blank to use the template's own subject.">
            <Input
              value={testForm.subject}
              onChange={(e) => setTestForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="[TEST] …"
              disabled={testBusy}
            />
          </FieldGroup>
          <FieldGroup label="Variables (JSON)" hint='e.g. {"recipient_name":"Sony","approval_title":"Test"}'>
            <Textarea
              mono
              rows={3}
              value={testForm.variables}
              onChange={(e) => setTestForm(f => ({ ...f, variables: e.target.value }))}
              disabled={testBusy}
            />
          </FieldGroup>
          {testError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
              {testError}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default function ResolvedTemplateCenterPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthBuildSectionEnabled()) return <TemplateCenterPage {...props} />;
  return <TemplateCenterPageV2Inner {...props} />;
}

export { TemplateCenterPageV2Inner };
